// Google Fonts fallback for when Medallia CDN (alchemy-fonts) is blocked.
// Keep GOOGLE_FONTS_NUNITO_URL in sync with:
// - public/nunito-font-fallback.js
// - app/javascript/solidSurvey/index.html (inline script)
const GOOGLE_FONTS_NUNITO_URL =
  'https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&display=swap';

const FONT_FALLBACK_LINK_ID = 'nunito-font-fallback';
const FONT_CHECK_TIMEOUT_MS = 3000;
const FONT_CHECK_SIZES = ['16px Nunito', '600 16px Nunito', '700 16px Nunito'];

let fallbackStarted = false;
let googleFontsRequested = false;

const verifyNunitoLoaded = async () => {
  if (!document.fonts?.load) {
    return false;
  }

  try {
    await Promise.race([
      Promise.all(FONT_CHECK_SIZES.map(size => document.fonts.load(size))),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('timeout')), FONT_CHECK_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return false;
  }

  return true;
};

const waitForFonts = () =>
  Promise.race([
    document.fonts?.ready ?? Promise.resolve(),
    new Promise(resolve => {
      setTimeout(resolve, FONT_CHECK_TIMEOUT_MS);
    }),
  ]);

const ensureGooglePreconnect = () => {
  if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
    const googlePreconnect = document.createElement('link');
    googlePreconnect.rel = 'preconnect';
    googlePreconnect.href = 'https://fonts.googleapis.com';
    document.head.appendChild(googlePreconnect);
  }

  if (!document.querySelector('link[href="https://fonts.gstatic.com"]')) {
    const gstaticPreconnect = document.createElement('link');
    gstaticPreconnect.rel = 'preconnect';
    gstaticPreconnect.href = 'https://fonts.gstatic.com';
    gstaticPreconnect.crossOrigin = '';
    document.head.appendChild(gstaticPreconnect);
  }
};

const loadGoogleFontsStylesheet = () => {
  let link = document.getElementById(FONT_FALLBACK_LINK_ID);

  if (!link) {
    link = document.createElement('link');
    link.id = FONT_FALLBACK_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }

  if (!link.getAttribute('href')) {
    link.href = GOOGLE_FONTS_NUNITO_URL;
  }
};

const waitForGoogleFonts = () =>
  Promise.race([
    Promise.all(FONT_CHECK_SIZES.map(size => document.fonts?.load?.(size) ?? Promise.resolve())),
    new Promise(resolve => {
      setTimeout(resolve, FONT_CHECK_TIMEOUT_MS);
    }),
  ]);

const loadGoogleFontsIfNeeded = async ({ force = false } = {}) => {
  if (googleFontsRequested) {
    return;
  }

  if (!force && (await verifyNunitoLoaded())) {
    return;
  }

  googleFontsRequested = true;
  ensureGooglePreconnect();
  loadGoogleFontsStylesheet();

  try {
    await waitForGoogleFonts();
  } catch {
    // Ignore font loading errors — the stylesheet may still apply later.
  }
};

const setupAlchemyFontStylesheetListener = onUnavailable => {
  const alchemyLink = document.querySelector('link[href*="alchemy-fonts"]');

  if (!alchemyLink) {
    return;
  }

  alchemyLink.addEventListener('error', onUnavailable);
};

export const loadNunitoFontFallback = async () => {
  if (fallbackStarted || typeof document === 'undefined') {
    return;
  }

  fallbackStarted = true;

  setupAlchemyFontStylesheetListener(() => {
    loadGoogleFontsIfNeeded({ force: true });
  });

  await waitForFonts();
  await loadGoogleFontsIfNeeded();
};
